/**
 * PULL.1 §6 — handheld mode detection.
 * Explicit `?mode=handheld` or viewport under 480 CSS px.
 *
 * Warehouse-scanner home (SVANTTO-class 4" PDA) is a tighter check:
 * Chrome never exposes the OEM name, so we fingerprint the 480×800
 * Android screen those units report. `?mode=handheld` opts a gun in;
 * `?mode=full` opts out and is remembered. Sellers never qualify.
 */

import { isTeamMemberUser } from "./employees";

export const HANDHELD_QUERY = "mode=handheld";
export const SCANNER_HOME_KEY = "riverr-scanner-home";

/**
 * Enterprise admins and staff only. Seller accounts (including a
 * public signup that only has roles:["member"]) never get the gun UI.
 * No uid yet means the profile has not loaded — do not guess.
 */
export const isHandheldStaffUser = (user) => isTeamMemberUser(user);

/** Signed-in seller (or anyone who is not admin/staff) on a gun route. */
export const shouldLeaveHandheld = ({ user, pathname = "", search = "" } = {}) => {
  if (!user?.uid || isHandheldStaffUser(user)) return false;
  return isHandheldTaskPath(pathname) || isHandheldSearch(search);
};

const modeFromSearch = (search = "") => {
  try {
    const q = new URLSearchParams(
      String(search || "").startsWith("?")
        ? String(search).slice(1)
        : String(search || "")
    );
    return String(q.get("mode") || "").toLowerCase();
  } catch {
    return "";
  }
};

export const isHandheldSearch = (search = "") => {
  const mode = modeFromSearch(search);
  return mode === "handheld" || mode === "hh";
};

export const isFullAppSearch = (search = "") => {
  const mode = modeFromSearch(search);
  return mode === "full" || mode === "desktop";
};

export const isHandheldViewport = () => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(max-width: 479px)").matches;
};

/** Prefer explicit query; otherwise auto when narrow. */
export const withHandheldQuery = (path) => {
  const base = String(path || "");
  if (!base) return `?${HANDHELD_QUERY}`;
  if (/[?&]mode=/i.test(base)) return base;
  return base.includes("?")
    ? `${base}&${HANDHELD_QUERY}`
    : `${base}?${HANDHELD_QUERY}`;
};

/** Parse `BATCH-{id}` from wedge / printed pull sheet. */
export const parseBatchBarcode = (raw) => {
  const s = String(raw || "").trim();
  if (!s) return null;
  const m =
    s.match(/^BATCH[_-]?([A-Za-z0-9_-]+)$/i) ||
    s.match(/\/batches\/([A-Za-z0-9_-]+)(?:\/pull)?/i);
  return m ? m[1] : null;
};

export const isAndroidUa = (ua = "") =>
  /Android/i.test(ua) && !/iPhone|iPad|iPod/i.test(ua);

/**
 * Physical short/long edges from `screen.width` (CSS px) × DPR.
 * `innerWidth` is the layout viewport and overlaps every phone, so it
 * must not count as a scanner by itself.
 */
const physicalEdges = ({
  screenWidth,
  screenHeight,
  devicePixelRatio = 1,
} = {}) => {
  const sw = Number(screenWidth) || 0;
  const sh = Number(screenHeight) || 0;
  const dpr = Number(devicePixelRatio) || 1;
  if (!sw || !sh) return null;
  return {
    short: Math.min(sw, sh) * dpr,
    long: Math.max(sw, sh) * dpr,
  };
};

/**
 * SVANTTO / Zebra / Honeywell 4" PDA class: ~480×800 physical.
 * Phones share the 360×640 CSS artboard (Galaxy 360×800, HD 360×640)
 * and must not match — their short edge is ≥720 physical px.
 */
export const isWarehouseScannerScreen = ({
  screenWidth,
  screenHeight,
  devicePixelRatio = 1,
} = {}) => {
  const edges = physicalEdges({ screenWidth, screenHeight, devicePixelRatio });
  if (!edges) return false;
  return (
    edges.short >= 400 &&
    edges.short <= 560 &&
    edges.long >= 700 &&
    edges.long <= 960
  );
};

/** Handset, not a 4" gun. Short edge ≥720 (HD and up). */
export const isPhoneClassScreen = ({
  screenWidth,
  screenHeight,
  devicePixelRatio = 1,
} = {}) => {
  const edges = physicalEdges({ screenWidth, screenHeight, devicePixelRatio });
  if (!edges) return false;
  return edges.short >= 680;
};

export const isWarehouseScannerDevice = ({
  userAgent = "",
  screenWidth,
  screenHeight,
  innerWidth,
  innerHeight,
  devicePixelRatio = 1,
} = {}) =>
  isAndroidUa(userAgent) &&
  isWarehouseScannerScreen({
    screenWidth,
    screenHeight,
    innerWidth,
    innerHeight,
    devicePixelRatio,
  });

export const readScannerHomePref = () => {
  if (typeof localStorage === "undefined") return null;
  try {
    return localStorage.getItem(SCANNER_HOME_KEY);
  } catch {
    return null;
  }
};

export const writeScannerHomePref = (value) => {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(SCANNER_HOME_KEY, value);
  } catch {
    /* ignore quota / private mode */
  }
};

/** Already inside a handheld task shell — do not overlay home. */
export const isHandheldTaskPath = (pathname = "") =>
  /\/putaway\/handheld/i.test(pathname) ||
  /\/batches\/[^/]+\/(pull|distribute|stage|staging)/i.test(pathname) ||
  pathname === "/handheld" ||
  pathname === "/handheld/floor" ||
  String(pathname).startsWith("/handheld/");

/** Desktop QC Regular / Shipping item page, or embroidery station. */
export const isDesktopStationPath = (pathname = "") =>
  /^\/batch-item(\/|$)/i.test(pathname) ||
  /^\/embroidery-station(\/|$)/i.test(pathname);

/**
 * Open the large-button warehouse home.
 * Query wins (`handheld` on, `full` off); else remembered pref;
 * else the 4" Android PDA screen heuristic.
 */
export const shouldOpenScannerHome = ({
  search = "",
  userAgent = "",
  screenWidth,
  screenHeight,
  devicePixelRatio = 1,
  pref,
  user,
} = {}) => {
  if (user !== undefined && !isHandheldStaffUser(user)) return false;
  if (isFullAppSearch(search)) return false;
  if (isHandheldSearch(search)) return true;
  if (pref === "off") return false;
  // Logout used to stamp this pref on any Android phone the old CSS
  // check called a gun. A handset must not stay in scanner home.
  if (pref === "on" && !isPhoneClassScreen({ screenWidth, screenHeight, devicePixelRatio })) {
    return true;
  }
  return isWarehouseScannerDevice({
    userAgent,
    screenWidth,
    screenHeight,
    devicePixelRatio,
  });
};

/**
 * Item-id scans go to the gun floor only on a 4" PDA screen (or
 * `?mode=handheld`). Sticky "scanner home" pref must not send a QC
 * Regular desktop to /handheld/floor. Code format is always item id.
 */
export const shouldRouteScanToGunFloor = ({
  search = "",
  userAgent = "",
  screenWidth,
  screenHeight,
  innerWidth,
  innerHeight,
  devicePixelRatio = 1,
  user,
} = {}) => {
  if (user !== undefined && !isHandheldStaffUser(user)) return false;
  if (isFullAppSearch(search)) return false;
  if (isHandheldSearch(search)) return true;
  return isWarehouseScannerDevice({
    userAgent,
    screenWidth,
    screenHeight,
    innerWidth,
    innerHeight,
    devicePixelRatio,
  });
};

/**
 * Send the gun to /handheld/floor on launch.
 * `?mode=full` is the only opt-out. A stale "full app" pref must not
 * keep the PDA on desktop Batches.
 */
export const shouldRedirectToFloor = ({
  pathname = "",
  search = "",
  userAgent = "",
  screenWidth,
  screenHeight,
  innerWidth,
  innerHeight,
  devicePixelRatio = 1,
  workstationMode,
  user,
} = {}) => {
  // Caller passed an account: only admins and staff. Omit `user` only
  // when the profile is not in play (unit tests, logged-out gun).
  if (user !== undefined && !isHandheldStaffUser(user)) return false;
  if (isHandheldTaskPath(pathname)) return false;
  // QC Regular / Shipping item page is the right screen for that scan.
  if (isDesktopStationPath(pathname)) return false;
  if (String(workstationMode || "").toLowerCase() === "qc") return false;
  if (isFullAppSearch(search)) return false;
  if (isHandheldSearch(search)) return true;
  return isWarehouseScannerDevice({
    userAgent,
    screenWidth,
    screenHeight,
    innerWidth,
    innerHeight,
    devicePixelRatio,
  });
};
